import { Badge } from "~/components/ui/badge";
import type { MeditationSetupOption } from "~/features/meditations/types";
import {
    estimateMeditationMinutes,
    getMeditationDescription,
} from "~/features/meditations/utils/meditation-catalog";
import type { PartTypeFamily } from "~/features/parts-similarity/utils/type-family";

import { PickerScreen } from "./picker-screen";
import { SelectableCard } from "./selectable-card";

interface MeditationPickerScreenProps {
    options: MeditationSetupOption[];
    selectedId: MeditationSetupOption["id"];
    family: PartTypeFamily;
    partType: string | null;
    pauseMultiplier: 1 | 2 | 3;
    onSelect: (option: MeditationSetupOption) => void;
    onBack: () => void;
}

function familyHint(count: number, family: PartTypeFamily): string {
    if (family === "unknown") return `${count} options for this part.`;
    const article =
        family === "exile" || family === "unattached-burden" ? "an" : "a";
    const label = family === "unattached-burden" ? "unattached burden" : family;
    return `${count} options for ${article} ${label}.`;
}

export function MeditationPickerScreen({
    options,
    selectedId,
    family,
    partType,
    pauseMultiplier,
    onSelect,
    onBack,
}: MeditationPickerScreenProps) {
    return (
        <PickerScreen
            title="Choose a meditation"
            hint={familyHint(options.length, family)}
            onBack={onBack}
            columns="three"
        >
            {options.map((option) => {
                // AIDEV-NOTE: ONE description element per card, already adapted to this part.
                // The clinical caveat used to be a second green callout below this line, which
                // made cards in a grid row wildly uneven; it is folded in here instead.
                const description = getMeditationDescription(option, {
                    family,
                    partType,
                });
                return (
                    <SelectableCard
                        key={option.id}
                        selected={selectedId === option.id}
                        onSelect={() => onSelect(option)}
                        imageUrl={option.imageUrl}
                    >
                        <span className="flex flex-wrap items-center gap-2">
                            {/* display-card-title (Playfair 20/26/600) per DESIGN.md —
                                Playfair is never used under 18px, so the old 16px
                                `text-base` title had to be either Inter or bigger. */}
                            <span className="font-display text-xl font-semibold leading-tight tracking-[-0.01em] text-brand-forest-900">
                                {option.title}
                            </span>
                            {/* AIDEV-NOTE: Standard Badge variants, not Eyebrows wearing
                                hand-rolled pill classes — same terra badge the setup
                                screen uses for the suggested meditation, so the two
                                surfaces agree. */}
                            {option.suggested ? (
                                <Badge variant="terra">Suggested</Badge>
                            ) : null}
                            {option.isDpmp ? (
                                <Badge variant="cream">DPMP®</Badge>
                            ) : null}
                            {/* AIDEV-NOTE: The reader tapped ONE part to get here, so the
                                session-wide meditation says so before they read the copy. */}
                            {option.backing === "session" ? (
                                <Badge variant="forest">Whole session</Badge>
                            ) : null}
                        </span>
                        <span className="mt-2 block font-body text-base leading-relaxed text-stone-600">
                            {description}
                        </span>
                        {/* body-sm, not 13px — guided UI keeps secondary copy at 14px.
                            mt-auto pins the meta line to the bottom so a card that runs a
                            line shorter than its neighbour still lines its footer up. */}
                        <span className="mt-auto block pt-3 font-body text-sm text-stone-500">
                            About{" "}
                            {estimateMeditationMinutes(option, pauseMultiplier)}{" "}
                            min
                            <span aria-hidden="true"> · </span>
                            {option.byLine}
                        </span>
                    </SelectableCard>
                );
            })}
        </PickerScreen>
    );
}
